import React, { FC } from 'react';
import styles from './DriverDetail.module.css';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { useLogUserCall } from '../../../services/useLogUserCall';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Badge, BadgeType } from '@components/atoms/Badge';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';

export interface DriverDetailProps {
    to_uuid?: string;
    icon?: string;
    src?: string;
    labelText?: string;
    distance?: string;
    status?: boolean;
    car?: string;
    vehicle_seat_number?: number;
    phone_number: string;
    onClose?: () => void;
}

export const DriverDetail: FC<DriverDetailProps> = ({
    to_uuid,
    icon,
    src,
    labelText,
    phone_number,
    distance,
    car,
    vehicle_seat_number,
    status,
    onClose,
}) => {
    const { t } = useTranslation();
    const { request: logEvent } = useLogUserCall();
    const translations = {
        distance: t('driver-detail.distance'),
        availability: t('driver-detail.availability'),
        vehicle: t('driver-detail.vehicle'),
        call: t('driver-detail.call'),
        whatsApp: t('driver-detail.whatsApp'),
        seats: t('driver-detail.seats'),
    };

    const handleCall = () => {
        logEvent({
            event_type_name: 'Chamada telefónica iniciada',
            action: 'ligou para ' + labelText,
            to_uuid: to_uuid ?? '',
        });
    };

    const handleWhatsapp = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const cleanNumber = phone_number?.replace(/\D/g, '');
        if (!cleanNumber) return;

        const numberWithCode = cleanNumber.length <= 9 ? '351' + cleanNumber : cleanNumber;

        const appUrl = `whatsapp://send?phone=${numberWithCode}`;
        window.location.href = appUrl;

        logEvent({
            event_type_name: 'Conversa no Whatsapp iniciada',
            action: ' contactou ' + labelText,
            to_uuid: to_uuid ?? '',
        });
    };

    return (
        <Container classes={styles.container}>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                <i className="fa-thin fa-xmark" />
            </button>

            <RoundProfilePicture {...{ icon, src, labelText }} />

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} className="mb-3">
                {distance != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography fontSize="16" color="muted" tagVariant={TypographyTagVariants.p}>
                            <span className={styles.iconLabel}>
                                <i className="fa-thin fa-ruler" />
                                {translations.distance}
                            </span>
                        </Typography>

                        <Typography fontSize="16" tagVariant={TypographyTagVariants.p}>
                            {distance}
                        </Typography>
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize="16" color="muted" tagVariant={TypographyTagVariants.p}>
                        <span className={styles.iconLabel}>
                            <i className="fa-thin fa-car-on" />
                            {translations.availability}
                        </span>
                    </Typography>
                    <Badge
                        type={status ? BadgeType.success : BadgeType.danger}
                        icon={status ? 'fa-light fa-check' : 'fa-light fa-xmark'}
                        label={status ? 'ON' : 'OFF'}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize="16" color="muted" tagVariant={TypographyTagVariants.p}>
                        <span className={styles.iconLabel}>
                            <i className="fa-thin fa-car" />
                            {translations.vehicle}
                        </span>
                    </Typography>
                    <Typography fontSize="16" tagVariant={TypographyTagVariants.p}>
                        {car}
                    </Typography>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize="16" color="muted" tagVariant={TypographyTagVariants.p}>
                        <span className={styles.iconLabel}>
                            <i className="fa-thin fa-users" />
                            {translations.seats}
                        </span>
                    </Typography>
                    <Typography fontSize="16" tagVariant={TypographyTagVariants.p}>
                        {vehicle_seat_number?.toString()}
                    </Typography>
                </div>
            </div>

            <Anchor mode={AnchorMode.primary} classes="w-100" onClick={handleWhatsapp}>
                <Typography fontSize="16" fontWeight="800" tagVariant={TypographyTagVariants.span}>
                    {translations.whatsApp}
                </Typography>
            </Anchor>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Anchor classes="w-100" tel={phone_number ?? ''} mode={AnchorMode.primary} onClick={handleCall}>
                <Typography fontSize="16" fontWeight="800" tagVariant={TypographyTagVariants.span}>
                    <>
                        {translations.call} {phone_number}
                    </>
                </Typography>
            </Anchor>
        </Container>
    );
};
